import React, { useEffect, useState } from 'react';
import { AlertTriangleIcon, SaveIcon, XIcon } from 'lucide-react';
import { CountrySelector } from '../../settings/CountrySelector';
import { CurrencySelector } from '../../settings/CurrencySelector';
export function TaxCodeForm({
  taxCode = null,
  onSave,
  onCancel,
  locale = 'en-US'
}) {
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    rate: 0.0,
    taxType: '',
    country: '',
    jurisdiction: '',
    currencyCode: '',
    isExempt: false,
    isInclusive: false,
    globalTaxDefinitionId: '',
    activeFrom: new Date().toISOString().split('T')[0],
    activeTo: ''
  });
  const [errors, setErrors] = useState({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  // Initialize form with existing tax code data if editing
  useEffect(() => {
    if (taxCode) {
      setFormData({
        code: taxCode.code || '',
        description: taxCode.description || '',
        rate: taxCode.rate || 0.0,
        taxType: taxCode.taxType || '',
        country: taxCode.country || '',
        jurisdiction: taxCode.jurisdiction || '',
        currencyCode: taxCode.currencyCode || '',
        isExempt: taxCode.isExempt || false,
        isInclusive: taxCode.isInclusive || false,
        globalTaxDefinitionId: taxCode.globalTaxDefinitionId || '',
        activeFrom: taxCode.activeFrom || new Date().toISOString().split('T')[0],
        activeTo: taxCode.activeTo || ''
      });
    }
  }, [taxCode]);
  const taxTypes = [{
    value: 'GST',
    label: 'Goods and Services Tax (GST)'
  }, {
    value: 'VAT',
    label: 'Value Added Tax (VAT)'
  }, {
    value: 'SalesTax',
    label: 'Sales Tax'
  }, {
    value: 'ConsumptionTax',
    label: 'Consumption Tax'
  }, {
    value: 'ServiceTax',
    label: 'Service Tax'
  }, {
    value: 'WithholdingTax',
    label: 'Withholding Tax'
  }, {
    value: 'Other',
    label: 'Other'
  }];
  const handleInputChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    if (type === 'checkbox') {
      setFormData({
        ...formData,
        [name]: checked
      });
      // If isExempt is checked, set rate to 0
      if (name === 'isExempt' && checked) {
        setFormData({
          ...formData,
          [name]: checked,
          rate: 0
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
    // Clear any error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  const handleCountryChange = country => {
    setFormData({
      ...formData,
      country
    });
    if (errors.country) {
      setErrors({
        ...errors,
        country: null
      });
    }
  };
  const handleCurrencyChange = currencyCode => {
    setFormData({
      ...formData,
      currencyCode
    });
    if (errors.currencyCode) {
      setErrors({
        ...errors,
        currencyCode: null
      });
    }
  };
  const validateForm = () => {
    const newErrors = {};
    // Required fields
    if (!formData.code) newErrors.code = 'Tax code is required';
    if (!formData.description) newErrors.description = 'Description is required';
    if (!formData.taxType) newErrors.taxType = 'Tax type is required';
    if (!formData.country) newErrors.country = 'Country is required';
    if (!formData.currencyCode) newErrors.currencyCode = 'Currency is required';
    if (!formData.activeFrom) newErrors.activeFrom = 'Active from date is required';
    // Rate validation (if not exempt)
    if (!formData.isExempt) {
      if (formData.rate === '') newErrors.rate = 'Rate is required';
      if (formData.rate < 0 || formData.rate > 1) {
        newErrors.rate = 'Rate must be between 0 and 1 (e.g., 0.10 for 10%)';
      }
    }
    // Date validation
    if (formData.activeFrom && formData.activeTo && formData.activeFrom > formData.activeTo) {
      newErrors.activeTo = 'Active to date must be after active from date';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = e => {
    e.preventDefault();
    if (validateForm()) {
      // If exempt, ensure rate is 0
      const finalFormData = {
        ...formData,
        rate: formData.isExempt ? 0 : parseFloat(formData.rate)
      };
      // If editing, preserve the ID and status
      if (taxCode) {
        onSave({
          ...taxCode,
          ...finalFormData
        });
      } else {
        onSave(finalFormData);
      }
    }
  };
  return <form onSubmit={handleSubmit} className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-9716">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-9717">
        <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9718">
          {taxCode ? 'Edit Tax Code' : 'Create New Tax Code'}
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500" data-id="element-9719">
          {taxCode ? 'Update the details of an existing tax code' : 'Enter the details to create a new tax code'}
        </p>
      </div>
      <div className="px-4 py-5 sm:p-6" data-id="element-9720">
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6" data-id="element-9721">
          {/* Basic Information */}
          <div className="sm:col-span-2" data-id="element-9722">
            <label htmlFor="code" className="block text-sm font-medium text-gray-700" data-id="element-9723">
              Code <span className="text-red-500" data-id="element-9724">*</span>
            </label>
            <div className="mt-1" data-id="element-9725">
              <input type="text" name="code" id="code" value={formData.code} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.code ? 'border-red-300' : ''}`} placeholder="e.g., GST10, VAT20" data-id="element-9726" />
              {errors.code && <p className="mt-1 text-sm text-red-600" data-id="element-9727">{errors.code}</p>}
            </div>
          </div>
          <div className="sm:col-span-4" data-id="element-9728">
            <label htmlFor="description" className="block text-sm font-medium text-gray-700" data-id="element-9729">
              Description <span className="text-red-500" data-id="element-9730">*</span>
            </label>
            <div className="mt-1" data-id="element-9731">
              <input type="text" name="description" id="description" value={formData.description} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.description ? 'border-red-300' : ''}`} placeholder="e.g., Goods and Services Tax" data-id="element-9732" />
              {errors.description && <p className="mt-1 text-sm text-red-600" data-id="element-9733">
                  {errors.description}
                </p>}
            </div>
          </div>
          <div className="sm:col-span-2" data-id="element-9734">
            <label htmlFor="taxType" className="block text-sm font-medium text-gray-700" data-id="element-9735">
              Tax Type <span className="text-red-500" data-id="element-9736">*</span>
            </label>
            <div className="mt-1" data-id="element-9737">
              <select id="taxType" name="taxType" value={formData.taxType} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.taxType ? 'border-red-300' : ''}`} data-id="element-9738">
                <option value="" data-id="element-9739">Select Tax Type</option>
                {taxTypes.map(type => <option key={type.value} value={type.value} data-id="element-9740">
                    {type.label}
                  </option>)}
              </select>
              {errors.taxType && <p className="mt-1 text-sm text-red-600" data-id="element-9741">{errors.taxType}</p>}
            </div>
          </div>
          <div className="sm:col-span-2" data-id="element-9742">
            <label htmlFor="country" className="block text-sm font-medium text-gray-700" data-id="element-9743">
              Country <span className="text-red-500" data-id="element-9744">*</span>
            </label>
            <div className="mt-1" data-id="element-9745">
              <CountrySelector value={formData.country} onChange={handleCountryChange} locale={locale} data-id="element-9746" />
              {errors.country && <p className="mt-1 text-sm text-red-600" data-id="element-9747">{errors.country}</p>}
            </div>
          </div>
          <div className="sm:col-span-2" data-id="element-9748">
            <label htmlFor="jurisdiction" className="block text-sm font-medium text-gray-700" data-id="element-9749">
              Jurisdiction
            </label>
            <div className="mt-1" data-id="element-9750">
              <input type="text" name="jurisdiction" id="jurisdiction" value={formData.jurisdiction} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="e.g., New York, California" data-id="element-9751" />
            </div>
          </div>
          <div className="sm:col-span-2" data-id="element-9752">
            <label htmlFor="currencyCode" className="block text-sm font-medium text-gray-700" data-id="element-9753">
              Currency <span className="text-red-500" data-id="element-9754">*</span>
            </label>
            <div className="mt-1" data-id="element-9755">
              <CurrencySelector value={formData.currencyCode} onChange={handleCurrencyChange} countryCode={formData.country} locale={locale} data-id="element-9756" />
              {errors.currencyCode && <p className="mt-1 text-sm text-red-600" data-id="element-9757">
                  {errors.currencyCode}
                </p>}
            </div>
          </div>
          <div className="sm:col-span-2" data-id="element-9758">
            <label htmlFor="rate" className="block text-sm font-medium text-gray-700" data-id="element-9759">
              Rate <span className="text-red-500" data-id="element-9760">*</span>
            </label>
            <div className="mt-1 relative rounded-md shadow-sm" data-id="element-9761">
              <input type="number" name="rate" id="rate" value={formData.rate} onChange={handleInputChange} disabled={formData.isExempt} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.rate ? 'border-red-300' : ''} ${formData.isExempt ? 'bg-gray-100' : ''}`} placeholder="0.10" step="0.01" min="0" max="1" data-id="element-9762" />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-9763">
                <span className="text-gray-500 sm:text-sm" data-id="element-9764">
                  {formData.isExempt ? 'Exempt' : '%'}
                </span>
              </div>
            </div>
            {errors.rate ? <p className="mt-1 text-sm text-red-600" data-id="element-9765">{errors.rate}</p> : <p className="mt-1 text-xs text-gray-500" data-id="element-9766">
                Enter as decimal (e.g., 0.10 for 10%)
              </p>}
          </div>
          <div className="sm:col-span-3" data-id="element-9767">
            <div className="flex items-center h-5" data-id="element-9768">
              <input id="isExempt" name="isExempt" type="checkbox" checked={formData.isExempt} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-9769" />
              <label htmlFor="isExempt" className="ml-2 block text-sm text-gray-700" data-id="element-9770">
                Tax Exempt
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1" data-id="element-9771">
              When checked, the rate will be set to 0
            </p>
          </div>
          <div className="sm:col-span-3" data-id="element-9772">
            <div className="flex items-center h-5" data-id="element-9773">
              <input id="isInclusive" name="isInclusive" type="checkbox" checked={formData.isInclusive} onChange={handleInputChange} className="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded" data-id="element-9774" />
              <label htmlFor="isInclusive" className="ml-2 block text-sm text-gray-700" data-id="element-9775">
                Tax Inclusive
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1" data-id="element-9776">
              When checked, the tax is included in the price
            </p>
          </div>
          {/* Toggle for advanced fields */}
          <div className="sm:col-span-6" data-id="element-9777">
            <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="inline-flex items-center px-2.5 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9778">
              {showAdvanced ? 'Hide Advanced Fields' : 'Show Advanced Fields'}
            </button>
          </div>
          {/* Advanced fields (collapsible) */}
          {showAdvanced && <>
              <div className="sm:col-span-6" data-id="element-9779">
                <hr className="my-2" data-id="element-9780" />
                <h4 className="text-sm font-medium text-gray-900 mb-4" data-id="element-9781">
                  Advanced Settings
                </h4>
              </div>
              <div className="sm:col-span-6" data-id="element-9782">
                <label htmlFor="globalTaxDefinitionId" className="block text-sm font-medium text-gray-700" data-id="element-9783">
                  Global Tax Definition ID
                </label>
                <div className="mt-1" data-id="element-9784">
                  <input type="text" name="globalTaxDefinitionId" id="globalTaxDefinitionId" value={formData.globalTaxDefinitionId} onChange={handleInputChange} className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md" placeholder="Optional reference ID" data-id="element-9785" />
                </div>
                <p className="mt-1 text-xs text-gray-500" data-id="element-9786">
                  Optional identifier for global tax definitions
                </p>
              </div>
              <div className="sm:col-span-3" data-id="element-9787">
                <label htmlFor="activeFrom" className="block text-sm font-medium text-gray-700" data-id="element-9788">
                  Active From <span className="text-red-500" data-id="element-9789">*</span>
                </label>
                <div className="mt-1" data-id="element-9790">
                  <input type="date" name="activeFrom" id="activeFrom" value={formData.activeFrom} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.activeFrom ? 'border-red-300' : ''}`} data-id="element-9791" />
                  {errors.activeFrom && <p className="mt-1 text-sm text-red-600" data-id="element-9792">
                      {errors.activeFrom}
                    </p>}
                </div>
              </div>
              <div className="sm:col-span-3" data-id="element-9793">
                <label htmlFor="activeTo" className="block text-sm font-medium text-gray-700" data-id="element-9794">
                  Active To
                </label>
                <div className="mt-1" data-id="element-9795">
                  <input type="date" name="activeTo" id="activeTo" value={formData.activeTo} onChange={handleInputChange} className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${errors.activeTo ? 'border-red-300' : ''}`} data-id="element-9796" />
                  {errors.activeTo && <p className="mt-1 text-sm text-red-600" data-id="element-9797">
                      {errors.activeTo}
                    </p>}
                </div>
              </div>
              {/* Display audit information if editing */}
              {taxCode && <>
                  <div className="sm:col-span-3" data-id="element-9798">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-9799">
                      Created By
                    </label>
                    <div className="mt-1" data-id="element-9800">
                      <input type="text" value={taxCode.createdBy} disabled className="shadow-sm block w-full sm:text-sm border-gray-300 rounded-md bg-gray-50" data-id="element-9801" />
                    </div>
                    <p className="mt-1 text-xs text-gray-500" data-id="element-9802">
                      {new Date(taxCode.createdAt).toLocaleString(locale)}
                    </p>
                  </div>
                  <div className="sm:col-span-3" data-id="element-9803">
                    <label className="block text-sm font-medium text-gray-700" data-id="element-9804">
                      Last Modified By
                    </label>
                    <div className="mt-1" data-id="element-9805">
                      <input type="text" value={taxCode.modifiedBy} disabled className="shadow-sm block w-full sm:text-sm border-gray-300 rounded-md bg-gray-50" data-id="element-9806" />
                    </div>
                    <p className="mt-1 text-xs text-gray-500" data-id="element-9807">
                      {new Date(taxCode.modifiedAt).toLocaleString(locale)}
                    </p>
                  </div>
                </>}
            </>}
        </div>
      </div>
      <div className="px-4 py-3 bg-gray-50 text-right sm:px-6 flex justify-end space-x-3" data-id="element-9808">
        <button type="button" onClick={onCancel} className="inline-flex justify-center py-2 px-4 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9809">
          <XIcon className="h-4 w-4 mr-1.5" data-id="element-9810" />
          Cancel
        </button>
        <button type="submit" className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-9811">
          <SaveIcon className="h-4 w-4 mr-1.5" data-id="element-9812" />
          {taxCode ? 'Update Tax Code' : 'Create Tax Code'}
        </button>
      </div>
    </form>;
}